import { useState } from 'react';

interface UseExpandableTextProps {
  content: string;
  maxLength: number;
  searchTerm?: string;
  isHtml?: boolean;
}

// Function to truncate HTML content while preserving structure
const truncateHtmlContent = (html: string, maxChars: number): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  let charCount = 0;
  const truncateNode = (node: Node): boolean => {
    if (charCount >= maxChars) return false;

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (charCount + text.length > maxChars) {
        const remainingChars = maxChars - charCount;
        node.textContent = text.slice(0, remainingChars) + '…';
        charCount = maxChars;
        return false;
      }
      charCount += text.length;
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const children = Array.from(node.childNodes);
      for (const child of children) {
        if (!truncateNode(child)) {
          // Remove remaining siblings
          const remainingSiblings = children.slice(children.indexOf(child) + 1);
          remainingSiblings.forEach((sibling) => sibling.remove());
          return false;
        }
      }
    }
    return true;
  };

  Array.from(tempDiv.childNodes).forEach((child) => truncateNode(child));
  return tempDiv.innerHTML;
};

// Function to extract plain text from HTML content
const getPlainTextFromHtml = (html: string): string => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

export function useExpandableText({ content, maxLength, searchTerm = '', isHtml = false }: UseExpandableTextProps) {
  const [expanded, setExpanded] = useState(false);

  const toggleExpansion = () => setExpanded((v) => !v);

  // Extract plain text for length calculations
  const plainText = isHtml ? getPlainTextFromHtml(content) : content;
  const shouldTruncate = plainText && !searchTerm && plainText.length > maxLength;

  // Handle truncation
  const truncatedContent =
    shouldTruncate && !expanded
      ? isHtml
        ? truncateHtmlContent(content, maxLength)
        : plainText.slice(0, maxLength) + '…'
      : content;

  return {
    expanded,
    shouldTruncate,
    toggleExpansion,
    truncatedContent,
    plainText,
  };
}
